/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message, Space } from 'antd';
import { Formik } from 'formik';
import { get, pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinderApplicationPrivateModuleSpecsFragment } from '../../../../api/fragments/FinderApplicationPrivateModuleSpecs';
import { type FinderApplicationPublicModuleSpecsFragment } from '../../../../api/fragments/FinderApplicationPublicModuleSpecs';
import {
    UpdateFinderApplicationPublicModuleDocument,
    type UpdateFinderApplicationPublicModuleMutation,
    type UpdateFinderApplicationPublicModuleMutationVariables,
} from '../../../../api/mutations/updateFinderApplicationModule';
import {
    ApplicationMarket,
    EventLeadOriginType,
    EventMediumType,
    type FinderApplicationModuleUpdateSettings,
    type FinderApplicationPrivateModuleUpdateSettings,
    PreferenceValue,
} from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import { hasValue } from '../../../../utilities/fp';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { hasAppointmentScenario, hasPaymentScenario } from '../modules/implementations/shared';
import CounterSettingDetails from '../shared/CounterSettingDetails';
import { newZealandMarketValidator, singaporeMarketValidator } from '../shared/validators';
import { getFinancingApplicationMarket } from '../StandardApplicationModulePage/StandardApplicationModuleMainDetails';
import FinderApplicationModuleConfigurationForm from './FinderApplicationModuleConfigurationForm';
import { type FinderApplicationModuleCounterKey } from './type';

const counterFields: FinderApplicationModuleCounterKey[] = [
    'leadCounter',
    'applicationCounter',
    'reservationCounter',
    'appointmentCounter',
    'insuranceCounter',
];

type FinderApplicationModuleConfigurationProps = {
    module: FinderApplicationPublicModuleSpecsFragment | FinderApplicationPrivateModuleSpecsFragment;
};

export type FinderApplicationModuleConfigurationFormValues = (
    | FinderApplicationModuleUpdateSettings
    | FinderApplicationPrivateModuleUpdateSettings
) &
    Partial<Pick<FinderApplicationPrivateModuleSpecsFragment, FinderApplicationModuleCounterKey>>;

export const getValidator = (isPublic: boolean) =>
    validators.compose(
        validators.requiredNonEmptyString('displayName'),
        validators.only(
            values => hasPaymentScenario(get('scenarios', values)),
            validators.compose(
                validators.requiredString('paymentSettingsId'),
                validators.requiredNumber('depositPrice')
            )
        ),
        validators.only(
            values => hasAppointmentScenario(values?.scenarios),
            validators.requiredString('appointmentModuleId')
        ),
        validators.only(values => hasValue('bankModuleId')(values), validators.requiredString('market')),
        validators.only(values => {
            const market = get('market', values);
            const marketType = get('marketType', values);

            return market === ApplicationMarket.Singapore && !!marketType;
        }, singaporeMarketValidator),
        validators.only(values => {
            const showInsuranceCalculator = get('showInsuranceCalculator', values);

            return showInsuranceCalculator;
        }, validators.requiredString('insuranceModuleId')),
        validators.only(values => {
            const displayFinanceCalculator = get('displayFinanceCalculator', values);

            return (
                displayFinanceCalculator === PreferenceValue.Yes ||
                displayFinanceCalculator === PreferenceValue.Optional
            );
        }, validators.requiredString('bankModuleId')),
        validators.only(values => {
            const market = get('market', values);
            const marketType = get('marketType', values);

            return market === ApplicationMarket.NewZealand && !!marketType;
        }, newZealandMarketValidator),
        validators.only(values => {
            const showVisitModelPageButton = get('showVisitModelPageButton', values);

            return showVisitModelPageButton === true;
        }, validators.requiredString('modelPageUrl')),
        validators.requiredArray('scenarios'),
        validators.requiredBoolean('isOcrEnabled'),
        validators.requiredBoolean('tradeIn'),
        validators.only(() => isPublic, validators.requiredString('assignee.defaultId')),
        validators.requiredBoolean('showResetKYCButton'),
        validators.only(
            ({ capModuleId }) => capModuleId,
            validators.compose(
                validators.requiredBoolean('capPrequalification'),
                validators.requiredStringEnum('leadOrigin', EventLeadOriginType),
                validators.requiredStringEnum('leadMedium', EventMediumType),
                validators.only(
                    ({ leadMedium }) => leadMedium !== EventMediumType.Walkin,
                    validators.requiredString('leadCampaignId')
                )
            )
        )
    );

const FinderApplicationModuleConfiguration = ({ module }: FinderApplicationModuleConfigurationProps) => {
    const { t } = useTranslation('finderApplicationModuleDetails');
    const apolloClient = useApolloClient();
    const validate = useValidator(getValidator(module.__typename === 'FinderApplicationPublicModule'));

    const initialValues: FinderApplicationModuleConfigurationFormValues = useMemo(
        () => ({
            ...pick(
                [
                    'displayName',
                    'testDrive',
                    'tradeIn',
                    'isTradeInAmountVisible',
                    'scenarios',
                    'depositPrice',
                    'paymentSettingsId',
                    'myInfoSettingId',
                    'liveChatSettingId',
                    'priceDisclaimer',
                    'reservationInstructions',
                    'promoCodeModuleId',
                    'market',
                    'showResetKYCButton',
                    'showFromValueOnVehicleDetails',
                    'bankDisplayPreference',
                    'finderVehicleConditions',
                    'porscheApprovedInfo',
                    'displayAppointmentDatepicker',
                    'appointmentModuleId',
                    'insurerDisplayPreference',
                    'insurerIds',
                    'insuranceModuleId',
                    'financingPreference',
                    'isInsuranceOptional',
                    'displayFinanceCalculator',
                    'showInsuranceCalculator',
                    'isOcrEnabled',
                    'showVisitModelPageButton',
                    'modelPageUrl',
                    'reservationPeriod',
                    'skipForDeposit',
                    'bankIds',
                    'capModuleId',
                    'isSearchCapCustomerOptional',
                    'capPrequalification',
                    'leadOrigin',
                    'leadMedium',
                    'leadCampaignId',
                    ...counterFields,
                ],
                module
            ),
            marketType: getFinancingApplicationMarket(module.marketType),
            bankModuleId: module?.bankModule?.id,
            ...(module.__typename === 'FinderApplicationPublicModule' ? { assignee: module.assignee } : null),
        }),
        [module]
    );

    const onSubmit = useHandleError(
        async (values: FinderApplicationModuleUpdateSettings) => {
            const { scenarios } = values;
            message.loading({
                content: t('finderApplicationModuleDetails:messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const settings: FinderApplicationModuleUpdateSettings = {
                ...pick(
                    [
                        'displayName',
                        'tradeIn',
                        'isTradeInAmountVisible',
                        'scenarios',
                        'assignee',
                        'myInfoSettingId',
                        'liveChatSettingId',
                        'priceDisclaimer',
                        'reservationInstructions',
                        'promoCodeModuleId',
                        'market',
                        'marketType',
                        'bankModuleId',
                        'showResetKYCButton',
                        'showFromValueOnVehicleDetails',
                        'bankDisplayPreference',
                        'finderVehicleConditions',
                        'porscheApprovedInfo',
                        'displayAppointmentDatepicker',
                        'appointmentModuleId',
                        'insurerDisplayPreference',
                        'insurerIds',
                        'insuranceModuleId',
                        'financingPreference',
                        'isInsuranceOptional',
                        'displayFinanceCalculator',
                        'showInsuranceCalculator',
                        'isOcrEnabled',
                        'showVisitModelPageButton',
                        'modelPageUrl',
                        'reservationPeriod',
                        'skipForDeposit',
                        'bankIds',
                        'capModuleId',
                        'isSearchCapCustomerOptional',
                        'capPrequalification',
                        'leadOrigin',
                        'leadMedium',
                        'leadCampaignId',
                    ],
                    values
                ),
                testDrive: hasAppointmentScenario(scenarios),
                paymentSettingsId: null,
                depositPrice: null,
                bankModuleId:
                    values.displayFinanceCalculator === PreferenceValue.Yes ||
                    values.displayFinanceCalculator === PreferenceValue.Optional
                        ? values.bankModuleId
                        : null,
                capModuleId: values.capModuleId || null,
            };

            if (hasPaymentScenario(scenarios)) {
                // assign value if deposit
                settings.paymentSettingsId = values.paymentSettingsId;
                settings.depositPrice = values.depositPrice;
            }

            await apolloClient
                .mutate<
                    UpdateFinderApplicationPublicModuleMutation,
                    UpdateFinderApplicationPublicModuleMutationVariables
                >({
                    mutation: UpdateFinderApplicationPublicModuleDocument,
                    variables: { moduleId: module.id, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('finderApplicationModuleDetails:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateForm" name="updateForm" onSubmitCapture={handleSubmit}>
                    <Space direction="vertical">
                        <FinderApplicationModuleConfigurationForm module={module} />
                        <CounterSettingDetails names={counterFields} />
                    </Space>
                </Form>
            )}
        </Formik>
    );
};

export default FinderApplicationModuleConfiguration;
